import { X, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { useError } from "@/contexts/ErrorContext.jsx";
import { useLocation } from "wouter";

/**
 * Error Banner component that displays errors from the ErrorContext
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {'top'|'bottom'} [props.position='top'] - Position of the banner
 * @returns {JSX.Element} Error banner component
 */
export function ErrorBanner({ className, position = "top" }) {
  const { errors, removeError, clearAllErrors } = useError();
  const [location] = useLocation();

  // Don't show the 401 error banner on unauthorized page - it is redundant.
  if (
    location === "/unauthorized"
    && Array.isArray(errors)
    && errors.length > 0
    && errors.filter((e) => e.details && e.details.includes("status code 401"))
  ) {
    return null;
  }
  if (errors.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed left-0 right-0 z-50 mx-auto max-w-7xl px-4",
        position === "top" ? "top-4" : "bottom-4",
        className
      )}>
      <div className='space-y-2'>
        {errors.map((error) => (
          <div
            key={error.id}
            className='flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 shadow-lg'
            role='alert'>
            <AlertCircle className='h-5 w-5 shrink-0 text-red-600' />
            <div className='flex-1 min-w-0'>
              <div className='font-medium text-red-900'>{error.message}</div>
              {error.details && (
                <div className='mt-1 text-sm text-red-700 whitespace-pre-wrap break-words'>
                  {error.details}
                </div>
              )}
              <div className='mt-1 text-xs text-red-600'>
                {error.timestamp.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </div>
            </div>
            <button
              onClick={() => removeError(error.id)}
              className='shrink-0 rounded-md p-1 text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
              aria-label='Dismiss error'>
              <X className='h-4 w-4' />
            </button>
          </div>
        ))}
        {errors.length > 1 && (
          <div className='flex justify-end'>
            <button
              onClick={clearAllErrors}
              className='text-sm text-red-700 hover:text-red-900 underline'>
              Dismiss all
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
